import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useOverflowEdge } from '~/shared/lib/useOverflowEdge';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import { FilmGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { FormaView } from '../model/formaView';

import styles from './Forma.module.css';

const SKELETON_ROWS = 4;
const SKELETON_SETS = 4;
/** The figure tracks every movement's table keeps, Spadek included, so none of them reflows another. */
const FIGURE_TRACKS = 3;

interface FormaSessionTableProps {
  readonly view: FormaView;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onOpenClip: (clipId: number) => void;
}

/**
 * Every session of the movement, newest first: one cell per set in the movement's own fields, the
 * session's figures (volume and e1RM for a lift, distance, time and pace for a run) and, for a lift,
 * its Spadek on the chosen line. A filmed set is a button that opens its recording. Executions only,
 * no plan line and no hit or miss.
 */
export function FormaSessionTable({ view, gate, failed, onOpenClip }: FormaSessionTableProps) {
  const { t } = useTranslation();
  // The grid never draws fewer set tracks than its wait does, so a movement of up to four sets
  // lands in the columns its skeleton stood in. A track past the movement's own sets stays blank.
  const sets = gate.waiting ? 0 : view.setColumns;
  const tracks = Math.max(SKELETON_SETS, sets);
  const columns = { '--forma-sets': String(tracks) } as CSSProperties;
  const blank = (index: number | string) => <span aria-hidden="true" key={`blank-${String(index)}`} />;
  // Figures stand at the right edge; the tracks a movement does not use stay blank before them.
  const figureCount = gate.waiting ? 0 : view.columns.length + (view.drop ? 1 : 0);
  const spare = Array.from({ length: FIGURE_TRACKS - figureCount }, (_, index) => blank(`figure-${String(index)}`));
  const main = view.columns.length - 1;
  // A tab stop only while it has something to scroll, so the keyboard can reach the far columns.
  const { ref, hasMore, hasPrevious } = useOverflowEdge<HTMLElement>('inline');

  return (
    <section
      aria-busy={gate.waiting}
      aria-label={t('web:forma.table.label')}
      className={`${String(scrollbar.scrollbar)} ${String(styles.sessionCard)}`}
      ref={ref}
      role="table"
      style={columns}
      tabIndex={hasMore || hasPrevious ? 0 : undefined}
    >
      <div className={styles.sessionGroup} role="rowgroup">
        <div className={`${String(styles.sessionGrid)} ${String(styles.sessionHead)}`} role="row">
          <span role="columnheader">{t('web:forma.table.session')}</span>
          {Array.from({ length: tracks }, (_, index) =>
            index < sets || gate.waiting ? (
              <span className={styles.sessionCentre} key={index} role="columnheader">
                {t('web:forma.table.set', { number: index + 1 })}
              </span>
            ) : (
              blank(index)
            ),
          )}
          {spare}
          {gate.waiting
            ? null
            : view.columns.map((label) => (
                <span className={styles.sessionFigure} key={label} role="columnheader">
                  {label}
                </span>
              ))}
          {!gate.waiting && view.drop ? (
            <span className={styles.sessionFigure} role="columnheader">
              {t('web:forma.table.drop')}
            </span>
          ) : null}
        </div>
      </div>
      <div
        className={`${String(styles.sessionGroup)} ${String(styles.sessionRows)} ${gate.reveal}`}
        role="rowgroup"
      >
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton
              ? Array.from({ length: SKELETON_ROWS }, (_, row) => (
                  <div className={styles.sessionGrid} key={row} role="row">
                    <i className={`${String(skel.bar)} ${String(styles.skelDate)}`} role="cell" />
                    {Array.from({ length: SKELETON_SETS }, (__, cell) => (
                      <i className={`${String(skel.bar)} ${String(styles.skelCell)}`} key={cell} role="cell" />
                    ))}
                    <span role="cell" />
                    <span role="cell" />
                    <span role="cell" />
                  </div>
                ))
              : null}
          </>
        ) : failed ? (
          <p className={styles.listNotice}>{t('web:forma.stage.failed')}</p>
        ) : (
          view.sessionRows.map((row) => (
            <div
              className={`${String(styles.sessionGrid)} ${String(styles.sessionRow)} ${row.current ? String(styles.sessionRowOn) : ''}`}
              key={row.workoutExecutionId}
              role="row"
            >
              <span className={styles.sessionDate} role="rowheader">
                {row.dateLabel}
              </span>
              {Array.from({ length: tracks }, (_, index) => {
                if (index >= sets) return blank(index);
                const cell = row.cells[index];
                if (cell === undefined) return <span key={index} role="cell" />;
                const clipId = cell.clipId;
                return (
                  <span key={cell.setId} role="cell">
                    {clipId === null ? (
                      <span className={styles.setCell}>{cell.label}</span>
                    ) : (
                      <button
                        aria-label={t('web:forma.table.openClip', {
                          set: index + 1,
                          date: row.dateLabel,
                        })}
                        className={`${String(styles.setCell)} ${String(styles.setCellFilmed)}`}
                        onClick={() => {
                          onOpenClip(clipId);
                        }}
                        type="button"
                      >
                        <FilmGlyph className={styles.setCellGlyph} />
                        {cell.label}
                      </button>
                    )}
                  </span>
                );
              })}
              {spare}
              {row.figures.map((figure, index) => (
                <span
                  className={`${String(styles.sessionFigure)} ${index === main ? String(styles.sessionFigureMain) : String(styles.sessionFigureMinor)}`}
                  key={view.columns[index]}
                  role="cell"
                >
                  {figure}
                </span>
              ))}
              {view.drop ? (
                <span
                  className={`${String(styles.sessionFigure)} ${String(styles.sessionDrop)} ${row.dropIsLoss ? String(styles.sessionDropLoss) : ''}`}
                  role="cell"
                >
                  {row.drop}
                </span>
              ) : null}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
